// Console redesign primitives, ported from customer-portal's
// components/console/ui.tsx. Styles live as .ps-* classes in index.css.

import React from 'react';
import { NavLink } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CONSOLE_GROUP, CONSOLE_NAV } from '../../config/consoleNav';

export const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ');

/** Main content column: padding + max width used on every console screen. */
export const Page: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cx('w-full max-w-[1280px] px-4 pt-5 pb-10 sm:px-7 sm:py-7 flex flex-col gap-5 sm:gap-6', className)}>
    {children}
  </div>
);

export const PageHeader: React.FC<{
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  eyebrow?: React.ReactNode;
}> = ({ title, subtitle, actions, eyebrow = `${CONSOLE_GROUP.label} · ${CONSOLE_GROUP.hint}` }) => (
  <div className="flex flex-wrap justify-between items-end gap-4">
    <div className="min-w-0">
      {eyebrow && <div className="ps-eyebrow">{eyebrow}</div>}
      <h1 className="mt-1.5 mb-0 text-2xl min-[821px]:text-[30px] font-medium text-ps-text">{title}</h1>
      {subtitle && <p className="mt-1.5 mb-0 text-ps-muted text-sm max-w-[720px] leading-relaxed">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
  </div>
);

/** Route-driven tabs across the TrainPods console pages. */
export const ConsoleTabs: React.FC = () => (
  <nav className="flex flex-wrap overflow-x-auto border-b border-ps-border" aria-label="TrainPods sections">
    {CONSOLE_NAV.map((t) => (
      <NavLink key={t.href} to={t.href} className={({ isActive }) => cx('ps-tab', isActive && 'ps-tab-on')}>
        {t.name}
      </NavLink>
    ))}
  </nav>
);

export type Tone = 'ok' | 'warn' | 'err' | 'off' | 'info' | 'accent';
// Spelled out in full so Tailwind's content scan keeps every tone class.
const TONE_CLASS: Record<Tone, string> = {
  ok: 'ps-tag-ok',
  warn: 'ps-tag-warn',
  err: 'ps-tag-err',
  off: 'ps-tag-off',
  info: 'ps-tag-info',
  accent: 'ps-tag-accent',
};
export const Tag: React.FC<{ tone?: Tone; className?: string; children: React.ReactNode }> = ({
  tone = 'off',
  className,
  children,
}) => <span className={cx('ps-tag', TONE_CLASS[tone], className)}>{children}</span>;

export const Chip: React.FC<{ on?: boolean; onClick?: () => void; children: React.ReactNode }> = ({
  on,
  onClick,
  children,
}) => (
  <button type="button" aria-pressed={!!on} onClick={onClick} className={cx('ps-chip', on && 'ps-chip-on')}>
    {children}
  </button>
);

export const StatTile: React.FC<{ label: React.ReactNode; value: React.ReactNode; sub?: React.ReactNode }> = ({
  label,
  value,
  sub,
}) => (
  <div className="ps-card p-4 flex flex-col gap-1.5 min-w-0">
    <span className="text-xs text-ps-subtle">{label}</span>
    <span className="ps-stat-value truncate">{value}</span>
    {sub && <span className="text-xs text-ps-subtle truncate">{sub}</span>}
  </div>
);

export const SectionHeader: React.FC<{ title: React.ReactNode; id?: string; action?: React.ReactNode }> = ({
  title,
  id,
  action,
}) => (
  <div className="flex flex-wrap justify-between items-baseline gap-2.5">
    <h2 id={id} className="m-0 text-[17px] font-medium text-ps-text">
      {title}
    </h2>
    {action && <div className="text-[13px]">{action}</div>}
  </div>
);

export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: React.ReactNode;
  body?: React.ReactNode;
  actions?: React.ReactNode;
  tone?: 'default' | 'error';
}> = ({ icon, title, body, actions, tone = 'default' }) => (
  <div className="ps-card px-6 py-10 flex flex-col items-center text-center gap-3">
    {icon && <span className="ps-num w-12 h-12 [&>svg]:w-[22px] [&>svg]:h-[22px]">{icon}</span>}
    <h2 className={cx('m-0 text-lg font-medium', tone === 'error' ? 'text-ps-err' : 'text-ps-text')}>{title}</h2>
    {body && <p className="m-0 text-ps-muted text-[13px] max-w-[460px] leading-relaxed">{body}</p>}
    {actions && <div className="flex gap-2.5 flex-wrap justify-center">{actions}</div>}
  </div>
);

/** Small centred message inside a card: loading / error lines. */
export const Notice: React.FC<{ children: React.ReactNode; tone?: 'default' | 'error' }> = ({
  children,
  tone = 'default',
}) => (
  <div className={cx('ps-card px-6 py-10 text-center text-[13px]', tone === 'error' ? 'text-ps-err' : 'text-ps-subtle')}>
    {children}
  </div>
);

/** Card that hosts a table and scrolls it horizontally on narrow screens. */
export const TableCard: React.FC<{ children: React.ReactNode; className?: string; footer?: React.ReactNode }> = ({
  children,
  className,
  footer,
}) => (
  <div className={cx('ps-card overflow-x-auto', className)}>
    <table className="ps-tbl">{children}</table>
    {footer && <div className="px-3.5 py-2.5 text-xs text-ps-subtle border-t border-ps-raised">{footer}</div>}
  </div>
);

/** One-line command with a Copy button (the artboards' `.copy`). */
export const CopyLine: React.FC<{ code: string }> = ({ code }) => (
  <div className="ps-copy">
    <code>{code}</code>
    <button
      type="button"
      className="ps-cb"
      aria-label="Copy to clipboard"
      onClick={() => {
        navigator.clipboard.writeText(code);
        toast.success('Copied');
      }}
    >
      Copy
    </button>
  </div>
);

export const formatINR = (n: number | undefined | null, digits = 2) => {
  const v = Number(n ?? 0);
  return `${v < 0 ? '-' : ''}₹${Math.abs(v).toLocaleString('en-IN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
};
